import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  Alert,
  TextInput,
  Select,
  Checkbox,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowRight,
  IconCalendar,
  IconCheck,
  IconCopy,
  IconCreditCard,
  IconDownload,
  IconEye,
  IconReceipt,
  IconRefresh,
  IconTrendingUp,
  IconWallet,
  IconX,
  IconUser,
  IconEdit,
  IconMapPin,
  IconTrash,
  IconMail,
  IconAlertCircle,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AnimatePresence, motion } from "framer-motion";
import jsPDF from "jspdf";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MegaLoader from "../../Components/MegaLoader";
import {
  Colors,
  FlutterwaveLogoSmall,
  HttpSuccessCodesObj,
  PaystackLogo,
  StripeLogo,
} from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { DefaultErrorNotification, DefaultSuccessNotification } from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  PaginationProps,
  SubscriptionProps,
  TransactionProps,
  StudentProps,
  PlanProps,
  CurrencyProps,
  PaymentProviderProps,
} from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import { AppContext } from "../Dashboard";
import { useForm } from "@mantine/form";
import StripeSupportedCountries from "../../Lib/countries";
import { useNavigate } from "react-router-dom";

dayjs.extend(relativeTime);

const Account: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const [pagination, setPagination] = useState<PaginationProps>({
    limit: 200,
    page: 1,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterProvider, setFilterProvider] = useState<string>("all");
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionProps | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [cancelModalOpened, { open: openCancelModal, close: closeCancelModal }] =
    useDisclosure(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<
    | (SubscriptionProps & {
        planName: string;
        price: number;
        currency: CurrencyProps;
      })
    | null
  >(null);
  const [addressModalOpened, { open: openAddressModal, close: closeAddressModal }] =
    useDisclosure(false);
  const [
    deleteAccountModalOpened,
    { open: openDeleteAccountModal, close: closeDeleteAccountModal },
  ] = useDisclosure(false);
  const [loading, setLoading] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteCheckboxChecked, setDeleteCheckboxChecked] = useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] = useState(false);

  const profile = context?.profile as StudentProps;

  const addressForm = useForm({
    initialValues: {
      street: "",
      city: "",
      country: "",
      zip: "",
    },
    validate: {
      street: (value) => (value.length > 0 ? null : "Street is required"),
      city: (value) => (value.length > 0 ? null : "City is required"),
      country: (value) => (value.length > 0 ? null : "Country is required"),
      zip: (value) => (value.length > 0 ? null : "Zip is required"),
    },
  });

  const openAddressModalWithData = () => {
    // Set form values with current profile address data
    addressForm.setValues({
      street: profile?.address?.street || "",
      city: profile?.address?.city || "",
      country: profile?.address?.country || "",
      zip: profile?.address?.zip || "",
    });
    openAddressModal();
  };

  const handleDeleteAccount = () => {
    setDeleteConfirmation("");
    setDeleteCheckboxChecked(false);
    openDeleteAccountModal();
  };

  const DeleteAccount = async () => {
    if (deleteConfirmation !== "I confirm the deletion of my account" || !deleteCheckboxChecked) {
      return;
    }

    setDeleteAccountLoading(true);
    const r = await PerformRequest({
      method: Endpoints.GetDeleteStudentAccount().method,
      route: Endpoints.GetDeleteStudentAccount().route,
      callback: () => {
        setDeleteAccountLoading(false);
      },
    });
    setDeleteAccountLoading(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Your account has been deleted!");
        context?.logout();
      } else {
        DefaultErrorNotification(r.message ?? "An error occurred");
      }
    }
  };

  const ResendVerificationEmail = async () => {
    setResendVerificationLoading(true);
    const r = await PerformRequest({
      method: Endpoints.GetResendVerificationEmail().method, // Using reset password endpoint for now
      route: Endpoints.GetResendVerificationEmail().route,
      data: {
        email: profile?.email,
      },
      callback: () => {
        setResendVerificationLoading(false);
      },
    });
    setResendVerificationLoading(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Verification email sent successfully!");
      } else {
        DefaultErrorNotification(r.message ?? "Failed to send verification email");
      }
    }
  };

  const UpdateAddress = async () => {
    const { hasErrors } = addressForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const address = addressForm.values;
      const r = await PerformRequest({
        method: Endpoints.GetStudentUpdateAddress().method,
        route: Endpoints.GetStudentUpdateAddress().route,
        data: {
          address: address,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          context?.reloadProfile();
          DefaultSuccessNotification("Your address has been updated!");
          closeAddressModal();
        }
      }
    }
  };

  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    reloadData: reloadTransactions,
  } = usePerformRequest<PaginatedResponse<TransactionProps[]>>({
    method: Endpoints.GetTransactions(pagination.page, pagination.limit).method,
    route: Endpoints.GetTransactions(pagination.page, pagination.limit).route,
  });

  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
    reloadData: reloadSubscriptions,
  } = usePerformRequest<
    NonPaginatedResponse<
      (SubscriptionProps & {
        planName: string;
        price: number;
        currency: CurrencyProps;
      })[]
    >
  >({
    method: Endpoints.GetSubscriptions().method,
    route: Endpoints.GetSubscriptions().route,
  });

  // Filter and search transactions
  const filteredTransactions = useMemo(() => {
    if (!transactions?.data) return [];

    return transactions.data.filter((transaction) => {
      const matchesSearch =
        searchQuery === "" ||
        transaction.reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transaction.id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = filterType === "all" || transaction.type === filterType;
      const matchesProvider = filterProvider === "all" || transaction.provider === filterProvider;

      return matchesSearch && matchesType && matchesProvider;
    });
  }, [transactions?.data, searchQuery, filterType, filterProvider]);

  const getProviderIcon = (provider: PaymentProviderProps) => {
    switch (provider) {
      case "paystack":
        return PaystackLogo;
      case "stripe":
        return StripeLogo;
      case "flutterwave":
        return FlutterwaveLogoSmall;
      default:
        return "";
    }
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case "paystack":
        return "blue";
      case "monnify":
        return "green";
      case "offline":
        return "gray";
      default:
        return "blue";
    }
  };

  const getTypeColor = (type: string) => {
    return type === "new" ? "green" : "orange";
  };

  const formatCurrency = (amount: number, currency?: CurrencyProps) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency ?? "USD",
    }).format(amount / 100);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    DefaultSuccessNotification(t("transactions.referenceCopied"));
  };

  const handleViewTransaction = (transaction: TransactionProps) => {
    setSelectedTransaction(transaction);
    openModal();
  };

  const handleCancelSubscription = (
    subscription: SubscriptionProps & {
      planName: string;
      price: number;
      currency: CurrencyProps;
    }
  ) => {
    setSubscriptionToCancel(subscription);
    openCancelModal();
  };

  const [isCancelling, setCancelling] = useState(false);
  const CancelSubscription = async () => {
    setCancelling(true);
    const r = await PerformRequest({
      method: Endpoints.GetCancelSubscription(subscriptionToCancel?.id as string).method,
      route: Endpoints.GetCancelSubscription(subscriptionToCancel?.id as string).route,
      callback: () => {
        setCancelling(false);
      },
    });
    setCancelling(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification(t("transactions.subscriptionCancelled"));
        reloadSubscriptions();
        closeCancelModal();
        setSubscriptionToCancel(null);
      }
    }
  };

  const downloadReceipt = (transaction: TransactionProps) => {
    const pdf = new jsPDF();

    // Set up the document
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Colors
    const primaryColor = "#7c3aed";
    const lightGray = "#6b7280";
    const darkGray = "#374151";

    // Header - Company Logo/Name
    pdf.setFillColor(124, 58, 237); // Purple background
    pdf.rect(0, 0, pageWidth, 40, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont("helvetica", "bold");
    pdf.text("Emril Academy", margin, 25);

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    pdf.text("Payment Receipt", pageWidth - margin - 40, 25);

    // Reset text color
    pdf.setTextColor(0, 0, 0);

    let yPosition = 60;

    // Receipt Title
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text("Transaction Receipt", margin, yPosition);

    yPosition += 20;

    // Transaction Status
    pdf.setFillColor(34, 197, 94); // Green background
    pdf.roundedRect(margin, yPosition, 60, 15, 3, 3, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.text("COMPLETED", margin + 15, yPosition + 10);

    yPosition += 30;

    // Amount - Large and prominent
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(28);
    pdf.setFont("helvetica", "bold");
    pdf.text(formatCurrency(transaction.amount, transaction.currency), margin, yPosition);

    yPosition += 25;

    // Divider line
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(229, 231, 235);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 20;

    // Transaction Details Section
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text("Transaction Details", margin, yPosition);

    yPosition += 15;

    // Details in two columns
    const leftCol = margin;
    const rightCol = pageWidth / 2 + 10;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");

    // Left column
    pdf.setTextColor(107, 114, 128);
    pdf.text("Transaction ID:", leftCol, yPosition);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(transaction.id, leftCol, yPosition + 8);

    // Right column
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(107, 114, 128);
    pdf.text("Date & Time:", rightCol, yPosition);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(
      dayjs(transaction.createdAt).format("MMMM DD, YYYY - HH:mm:ss"),
      rightCol,
      yPosition + 8
    );

    yPosition += 25;

    // Payment Reference
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(107, 114, 128);
    pdf.text("Payment Reference:", leftCol, yPosition);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(transaction.reference, leftCol, yPosition + 8);

    // Transaction Type
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(107, 114, 128);
    pdf.text("Transaction Type:", rightCol, yPosition);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(transaction.type === "new" ? "New Purchase" : "Renewal", rightCol, yPosition + 8);

    yPosition += 25;

    // Payment Method
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(107, 114, 128);
    pdf.text("Payment Method:", leftCol, yPosition);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(
      transaction.provider.charAt(0).toUpperCase() + transaction.provider.slice(1),
      leftCol,
      yPosition + 8
    );

    yPosition += 30;

    // Another divider
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(229, 231, 235);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 20;

    // Footer information
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(107, 114, 128);

    pdf.text("Thank you for choosing Emril Academy!", margin, yPosition);
    pdf.text("For support, contact us at info@emrilacademy.com", margin, yPosition + 10);
    pdf.text(
      `Receipt generated on ${dayjs().format("MMMM DD, YYYY at HH:mm")}`,
      margin,
      yPosition + 20
    );

    // Footer - Page number and company info
    pdf.setFontSize(8);
    pdf.text("Emril Academy • Premium Online Learning Platform", margin, pageHeight - 15);
    pdf.text("Page 1 of 1", pageWidth - margin - 20, pageHeight - 15);

    // Save the PDF
    const fileName = `Emril-Receipt-${transaction.reference}-${dayjs().format("YYYY-MM-DD")}.pdf`;
    pdf.save(fileName);

    // Show success notification
    DefaultSuccessNotification(t("transactions.receiptDownloaded"));
  };

  const TransactionCard = ({ transaction }: { transaction: TransactionProps }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -2 }}
    >
      <Card className="transaction-card" shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md" style={{ height: "100%", justifyContent: "space-between" }}>
          <div>
            <Group justify="space-between" align="flex-start" mb="md">
              <div style={{ flex: 1 }}>
                <Group gap="xs" mb="xs">
                  <Badge
                    color={getTypeColor(transaction.type)}
                    variant="light"
                    size="sm"
                    radius="sm"
                  >
                    {transaction.type === "new"
                      ? t("transactions.newPurchase")
                      : t("transactions.renewal")}
                  </Badge>
                  <Badge
                    color={getProviderColor(transaction.provider)}
                    variant="outline"
                    size="sm"
                    radius="sm"
                  >
                    {transaction.provider}
                  </Badge>
                </Group>

                <Text size="lg" fw={600} c="dark.8" mb="xs">
                  {formatCurrency(transaction.amount, transaction.currency)}
                </Text>

                <Text size="sm" c="dimmed" mb="xs">
                  Reference: {transaction.reference}
                </Text>

                <Text size="sm" c="dimmed">
                  {dayjs(transaction.createdAt).format("MMM DD, YYYY • HH:mm")}
                </Text>
              </div>
            </Group>
          </div>

          <div>
            <Divider mb="md" />

            <Group justify="space-between" align="center">
              <Group gap="xs">
                <ThemeIcon variant="light" color="green" size="sm">
                  <IconCheck size={12} />
                </ThemeIcon>
                <Text size="sm" c="green.7" fw={500}>
                  {t("transactions.completed")}
                </Text>
              </Group>

              <Button
                variant="light"
                size="xs"
                color="violet"
                rightSection={<IconArrowRight size={14} />}
                onClick={() => handleViewTransaction(transaction)}
              >
                {t("transactions.viewDetails")}
              </Button>
            </Group>
          </div>
        </Stack>
      </Card>
    </motion.div>
  );

  const TransactionModal = () => (
    <Modal
      opened={modalOpened}
      onClose={closeModal}
      title={
        <Group gap="sm">
          <ThemeIcon variant="light" color="violet" size="lg">
            <IconReceipt size={20} />
          </ThemeIcon>
          <div>
            <Text size="lg" fw={600}>
              {t("transactions.transactionDetails")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("transactions.transactionId")}: {selectedTransaction?.id}
            </Text>
          </div>
        </Group>
      }
      size="lg"
      radius="md"
      centered
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      {selectedTransaction && (
        <Stack gap="xl">
          {/* Amount and Status */}
          <Paper withBorder p="xl" radius="md" className="transaction-summary">
            <Center>
              <Stack align="center" gap="md">
                <ThemeIcon variant="light" color="green" size={60}>
                  <IconCheck size={30} />
                </ThemeIcon>
                <div style={{ textAlign: "center" }}>
                  <Text size="xl" fw={700} c="green.7" mb="xs">
                    {t("transactions.paymentSuccessful")}
                  </Text>
                  <Text size="2xl" fw={900} c="dark.8">
                    {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                  </Text>
                </div>
              </Stack>
            </Center>
          </Paper>

          {/* Transaction Info Grid */}
          <Grid gutter="lg">
            <Grid.Col span={6}>
              <Paper withBorder p="md" radius="md" h={120}>
                <Group gap="sm" mb="xs">
                  <IconCalendar size={16} color={Colors.PurplePrimary} />
                  <Text size="sm" fw={500} c="dark.6">
                    {t("transactions.dateTime")}
                  </Text>
                </Group>
                <Text size="lg" fw={600}>
                  {dayjs(selectedTransaction.createdAt).format("MMMM DD, YYYY")}
                </Text>
                <Text size="sm" c="dimmed">
                  {dayjs(selectedTransaction.createdAt).format("HH:mm:ss")}
                </Text>
              </Paper>
            </Grid.Col>

            <Grid.Col span={6}>
              <Paper h={120} withBorder p="md" radius="md">
                <Group gap="sm" mb="xs">
                  <IconCreditCard size={16} color={Colors.PurplePrimary} />
                  <Text size="sm" fw={500} c="dark.6">
                    {t("transactions.paymentMethod")}
                  </Text>
                </Group>
                <div className="flex-col">
                  <img
                    src={getProviderIcon(selectedTransaction.provider)}
                    className="transaction-provider-image"
                    alt="Provider Icon"
                  />
                  <Text size="lg" fw={600} tt="capitalize">
                    {selectedTransaction.provider}
                  </Text>
                </div>
              </Paper>
            </Grid.Col>

            <Grid.Col span={6}>
              <Paper withBorder p="md" radius="md">
                <Group gap="sm" mb="xs">
                  <IconRefresh size={16} color={Colors.PurplePrimary} />
                  <Text size="sm" fw={500} c="dark.6">
                    {t("transactions.transactionType")}
                  </Text>
                </Group>
                <Badge color={getTypeColor(selectedTransaction.type)} variant="light" size="lg">
                  {selectedTransaction.type === "new"
                    ? t("transactions.newPurchase")
                    : t("transactions.renewal")}
                </Badge>
              </Paper>
            </Grid.Col>

            <Grid.Col span={6}>
              <Paper withBorder p="md" radius="md">
                <Group gap="sm" mb="xs">
                  <IconTrendingUp size={16} color={Colors.PurplePrimary} />
                  <Text size="sm" fw={500} c="dark.6">
                    {t("transactions.status")}
                  </Text>
                </Group>
                <Group gap="xs">
                  <ThemeIcon variant="light" color="green" size="sm">
                    <IconCheck size={12} />
                  </ThemeIcon>
                  <Text size="lg" fw={600} c="green.7">
                    {t("transactions.completed")}
                  </Text>
                </Group>
              </Paper>
            </Grid.Col>
          </Grid>

          {/* Reference Information */}
          <Paper withBorder p="md" radius="md">
            <Text size="sm" fw={500} c="dark.6" mb="xs">
              {t("transactions.paymentReference")}
            </Text>
            <Group justify="space-between" align="center">
              <Text size="lg" fw={600} style={{ fontFamily: "monospace" }}>
                {selectedTransaction.reference}
              </Text>
              <Tooltip label={t("transactions.copyReference")}>
                <ActionIcon
                  variant="subtle"
                  color="violet"
                  onClick={() => copyToClipboard(selectedTransaction.reference, "Reference")}
                >
                  <IconCopy size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Paper>

          <Divider />

          {/* Action Buttons */}
          <Group justify="center" gap="md">
            <Button
              variant="light"
              color="violet"
              leftSection={<IconDownload size={16} />}
              size="md"
              onClick={() => downloadReceipt(selectedTransaction)}
            >
              {t("transactions.downloadReceipt")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );

  if (isLoadingTransactions) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <MegaLoader size={60} />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl" className="transactions-container">
      <Tabs defaultValue="transactions" variant="default">
        <Tabs.List grow>
          <Tabs.Tab value="transactions" leftSection={<IconReceipt size={16} />}>
            {t("navigation.transactions")}
          </Tabs.Tab>
          <Tabs.Tab value="billing" leftSection={<IconCreditCard size={16} />}>
            {t("navigation.billing")}
          </Tabs.Tab>
          <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
            {t("common.profile")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="transactions">
          <br />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <Paper withBorder p="xl" radius="md" mb="xl" className="transactions-header">
              <Group justify="space-between" align="center" mb="lg">
                <div>
                  <Title order={1} fw={700} mb="xs">
                    {t("navigation.transactions")}
                  </Title>
                  <Text size="lg" c="dimmed">
                    {t("transactions.subtitle")}
                  </Text>
                </div>
                <Button
                  variant="light"
                  color="violet"
                  leftSection={<IconRefresh size={16} />}
                  onClick={reloadTransactions}
                >
                  {t("transactions.refresh")}
                </Button>
              </Group>

              {/* Stats Cards */}
              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper withBorder p="md" radius="md" className="stat-card">
                    <Stack align="center" gap="sm">
                      <ThemeIcon variant="light" color="blue" size="xl">
                        <IconReceipt size={24} />
                      </ThemeIcon>
                      <div style={{ textAlign: "center" }}>
                        <Text size="sm" c="dimmed" fw={500}>
                          {t("transactions.totalTransactions")}
                        </Text>
                        <Text size="xl" fw={700}>
                          {transactions?.data?.length || 0}
                        </Text>
                      </div>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper withBorder p="md" radius="md" className="stat-card">
                    <Stack align="center" gap="sm">
                      <ThemeIcon variant="light" color="green" size="xl">
                        <IconTrendingUp size={24} />
                      </ThemeIcon>
                      <div style={{ textAlign: "center" }}>
                        <Text size="sm" c="dimmed" fw={500}>
                          {t("transactions.totalSpent")}
                        </Text>
                        <Text size="xl" fw={700}>
                          {formatCurrency(
                            filteredTransactions.reduce((sum, t) => sum + t.amount, 0),
                            filteredTransactions[0]?.currency
                          )}
                        </Text>
                      </div>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper withBorder p="md" radius="md" className="stat-card">
                    <Stack align="center" gap="sm">
                      <ThemeIcon variant="light" color="orange" size="xl">
                        <IconRefresh size={24} />
                      </ThemeIcon>
                      <div style={{ textAlign: "center" }}>
                        <Text size="sm" c="dimmed" fw={500}>
                          {t("transactions.renewals")}
                        </Text>
                        <Text size="xl" fw={700}>
                          {filteredTransactions.filter((t) => t.type === "renewal").length}
                        </Text>
                      </div>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper withBorder p="md" radius="md" className="stat-card">
                    <Stack align="center" gap="sm">
                      <ThemeIcon variant="light" color="violet" size="xl">
                        <IconCreditCard size={24} />
                      </ThemeIcon>
                      <div style={{ textAlign: "center" }}>
                        <Text size="sm" c="dimmed" fw={500}>
                          {t("transactions.newPurchases")}
                        </Text>
                        <Text size="xl" fw={700}>
                          {filteredTransactions.filter((t) => t.type === "new").length}
                        </Text>
                      </div>
                    </Stack>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Transactions Grid */}
            {filteredTransactions.length === 0 ? (
              <Paper withBorder p="xl" radius="md">
                <Center>
                  <Stack align="center" gap="md">
                    <ThemeIcon variant="light" color="gray" size={80}>
                      <IconReceipt size={40} />
                    </ThemeIcon>
                    <div style={{ textAlign: "center" }}>
                      <Text size="xl" fw={600} mb="xs">
                        {t("transactions.noTransactionsFound")}
                      </Text>
                      <Text c="dimmed">
                        {searchQuery || filterType !== "all" || filterProvider !== "all"
                          ? t("transactions.tryAdjustingFilters")
                          : t("transactions.historyAppearHere")}
                      </Text>
                    </div>
                  </Stack>
                </Center>
              </Paper>
            ) : (
              <Grid gutter="lg">
                <AnimatePresence>
                  {filteredTransactions.map((transaction, index) => (
                    <Grid.Col key={transaction.id} span={{ base: 12, sm: 6, lg: 4 }}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <TransactionCard transaction={transaction} />
                      </motion.div>
                    </Grid.Col>
                  ))}
                </AnimatePresence>
              </Grid>
            )}

            <TransactionModal />
          </motion.div>
        </Tabs.Panel>
        <Tabs.Panel value="billing" w="100%">
          {/* Billing Info and Subscriptions UI (dummy data) */}
          <div className="billing-info-container">
            <Paper withBorder p="xl" radius="md" mb="xl" className="billing-info-card">
              <Group align="flex-start" gap="xl">
                <Avatar
                  size={80}
                  radius={40}
                  children={context?.profile?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")}
                  alt="User"
                />
                <Stack gap={4}>
                  <Title order={3}>{context?.profile?.name}</Title>
                  <Text c="dimmed">{context?.profile?.email}</Text>

                  {/* <Text size="sm">
                    Card:{" "}
                    <b>
                      {context?.paymentMethod?.card.brand} •••• {context?.paymentMethod?.card.last4}
                    </b>{" "}
                    (Exp: {context?.paymentMethod?.card.exp_month}/
                    {context?.paymentMethod?.card.exp_year})
                  </Text> */}
                </Stack>
              </Group>
            </Paper>

            <Title order={4} mb="md">
              Your Subscriptions
            </Title>
            <Stack gap="md">
              {subscriptions?.data?.map((sub) => (
                <Card key={sub.id} withBorder p="lg" radius="md" className="subscription-card">
                  <Group justify="space-between" align="center">
                    <div>
                      <Text fw={600} size="lg">
                        {sub.planName}
                      </Text>
                      <Text c="dimmed" size="sm">
                        {formatCurrency(parseInt(sub.amount) || 0, sub.currency)} / month
                      </Text>
                      <Text size="sm">
                        {sub.cancelAtPeriodEnd ? "Ends at:" : "Next billing:"}
                        &nbsp;
                        <b>{dayjs(sub.currentPeriodEnd).format("DD/MM/YYYY")}</b>
                      </Text>
                      <Group gap="xs" mt={4}>
                        <Badge color={sub.status === "active" ? "green" : "gray"} variant="light">
                          {sub.status === "active" ? "Active" : "Canceled"}
                        </Badge>
                      </Group>
                    </div>
                    <Group gap="sm">
                      {sub.status === "active" && (
                        <Button
                          color="red"
                          variant="light"
                          size="sm"
                          radius="md"
                          onClick={() => handleCancelSubscription(sub)}
                        >
                          Cancel
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="profile" w="100%">
          <br />
          <motion.div
            initial={{ opacity: 0, y: 30, width: "100%" }}
            animate={{ opacity: 1, y: 0, width: "100%" }}
            transition={{ duration: 0.6 }}
          >
            {/* Profile Header */}
            <Paper withBorder p="xl" radius="md" mb="xl" className="profile-header">
              <Group justify="space-between" align="center" mb="lg">
                <div>
                  <Title order={1} fw={700} mb="xs">
                    {t("common.profile")}
                  </Title>
                  <Text size="lg" c="dimmed">
                    {t("account.manageInfo")}
                  </Text>
                </div>
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={16} />}
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </Button>
              </Group>

              {/* Email Verification Alert */}
              {!profile?.isEmailVerified && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  style={{ marginBottom: "1.5rem" }}
                >
                  <Alert
                    icon={<IconAlertCircle size="1rem" />}
                    title={t("account.emailNotVerified.title")}
                    color="orange"
                    variant="light"
                    withCloseButton={false}
                  >
                    <Stack gap="sm">
                      <Text size="sm">
                        {t("account.emailNotVerified.message", { email: profile?.email })}
                      </Text>
                      <Group gap="sm">
                        <Button
                          size="sm"
                          variant="filled"
                          color="orange"
                          loading={resendVerificationLoading}
                          onClick={ResendVerificationEmail}
                          leftSection={<IconMail size={14} />}
                        >
                          {t("account.emailNotVerified.resend")}
                        </Button>
                        <Text size="xs" c="dimmed">
                          {t("account.emailNotVerified.spamHint")}
                        </Text>
                      </Group>
                    </Stack>
                  </Alert>
                </motion.div>
              )}

              {/* Profile Info */}
              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, md: 12 }}>
                  <Paper withBorder p="md" radius="md" h={180} className="profile-info-card">
                    <Stack gap="md">
                      <Group gap="sm">
                        <IconUser size={20} color={Colors.PurplePrimary} />
                        <Text size="lg" fw={600}>
                          {t("account.personalInformation")}
                        </Text>
                      </Group>
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            {t("common.name")}:
                          </Text>
                          <Text size="sm" fw={600}>
                            {profile?.name}
                          </Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            {t("common.email")}:
                          </Text>
                          <Group gap="xs">
                            <Text size="sm" fw={600}>
                              {profile?.email}
                            </Text>
                            {profile?.isEmailVerified ? (
                              <Badge color="green" variant="light">
                                <IconCheck size={12} />
                              </Badge>
                            ) : (
                              <Button
                                variant="subtle"
                                color="red"
                                size="xs"
                                loading={resendVerificationLoading}
                                onClick={ResendVerificationEmail}
                                leftSection={<IconMail size={12} />}
                              >
                                {t("account.emailNotVerified.resendShort")}
                              </Button>
                            )}
                          </Group>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            {t("account.joined")}
                          </Text>
                          <Text size="sm" fw={600}>
                            {dayjs(profile?.createdAt).format("MMMM DD, YYYY")}
                          </Text>
                        </Group>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  {/* <Paper withBorder p="md" radius="md" h={180} className="address-info-card">
                    <Stack gap="md">
                      <Group gap="sm" justify="space-between">
                        <Group gap="sm">
                          <IconMapPin size={20} color={Colors.PurplePrimary} />
                          <Text size="lg" fw={600}>
                            Your Address
                          </Text>
                        </Group>
                        <Button
                          variant="light"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={openAddressModalWithData}
                        >
                          Edit
                        </Button>
                      </Group>
                      <Stack gap="xs">
                        {profile?.address?.street ? (
                          <>
                            <Text size="sm">{profile?.address?.street}</Text>
                            <Text size="sm">
                              {profile?.address?.city}, {profile?.address?.country}{" "}
                              {profile?.address?.zip}
                            </Text>
                          </>
                        ) : (
                          <Text size="sm" c="dimmed">
                            No address information available
                          </Text>
                        )}
                      </Stack>
                    </Stack>
                  </Paper> */}
                </Grid.Col>
              </Grid>
            </Paper>
          </motion.div>

          {/* Address Edit Modal */}
          <Modal
            opened={addressModalOpened}
            onClose={closeAddressModal}
            title={
              <Group gap={8}>
                <IconMapPin size={20} />
                Edit Address
              </Group>
            }
            centered
            size="sm"
            overlayProps={{ blur: 2, opacity: 0.5 }}
          >
            <form onSubmit={addressForm.onSubmit(() => UpdateAddress())}>
              <Stack gap="md">
                <TextInput
                  label="Street Address"
                  placeholder="Enter your street"
                  {...addressForm.getInputProps("street")}
                  required
                />
                <Group gap="md">
                  <TextInput
                    label="City"
                    placeholder="Enter your city"
                    {...addressForm.getInputProps("city")}
                    required
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    label="Zip"
                    placeholder="Enter your zip"
                    {...addressForm.getInputProps("zip")}
                    required
                    style={{ flex: 1 }}
                  />
                </Group>
                <Select
                  label="Country"
                  searchable
                  placeholder="Select country"
                  data={StripeSupportedCountries}
                  {...addressForm.getInputProps("country")}
                  required
                />
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="default"
                    onClick={closeAddressModal}
                    leftSection={<IconX size={16} />}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" loading={loading} leftSection={<IconCheck size={16} />}>
                    Save
                  </Button>
                </Group>
              </Stack>
            </form>
          </Modal>
        </Tabs.Panel>
      </Tabs>

      {/* Cancel Subscription Confirmation Modal */}
      <Modal
        opened={cancelModalOpened}
        onClose={closeCancelModal}
        title={
          <Group gap="sm">
            <ThemeIcon variant="light" color="red" size="lg">
              <IconX size={20} />
            </ThemeIcon>
            <div>
              <Text size="lg" fw={600}>
                Cancel Subscription
              </Text>
              <Text size="sm" c="dimmed">
                Are you sure you want to cancel your subscription?
              </Text>
            </div>
          </Group>
        }
        size="md"
        radius="md"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        {subscriptionToCancel && (
          <Stack gap="xl">
            {/* Warning Alert */}
            <Alert
              icon={<IconX size="1rem" />}
              title="Important Information"
              color="red"
              variant="light"
            >
              <Text size="sm" mb="xs">
                <b>What happens when you cancel:</b>
              </Text>
              <Stack gap="xs">
                <Text size="sm">
                  • You'll lose access to all plan features at the end of your current billing
                  period
                </Text>
                <Text size="sm">
                  • Your subscription will remain active until{" "}
                  {dayjs(subscriptionToCancel.currentPeriodEnd).format("MMMM DD, YYYY")}
                </Text>
                <Text size="sm">
                  • You can reactivate your subscription anytime before the end date
                </Text>
                <Text size="sm">• No refunds will be issued for the current period</Text>
              </Stack>
            </Alert>

            {/* Subscription Details */}
            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={500} c="dark.6" mb="xs">
                Subscription Details
              </Text>
              <Group justify="space-between" mb="xs">
                <Text size="sm">Plan:</Text>
                <Text size="sm" fw={600}>
                  {context?.plans?.find((p) => p.id === subscriptionToCancel.planID)?.name}
                </Text>
              </Group>
              <Group justify="space-between" mb="xs">
                <Text size="sm">Current Period Ends:</Text>
                <Text
                  size="sm"
                  fw={600}
                  c={
                    dayjs(subscriptionToCancel.currentPeriodEnd).isBefore(dayjs()) ? "red" : "green"
                  }
                >
                  {dayjs(subscriptionToCancel.currentPeriodEnd).format("MMMM DD, YYYY")}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Monthly Cost:</Text>
                <Text size="sm" fw={600}>
                  {formatCurrency(subscriptionToCancel.price * 100, subscriptionToCancel.currency)}
                </Text>
              </Group>
            </Paper>

            <Divider />

            {/* Action Buttons */}
            <div className="flex-row align-center justify-between width-100">
              <Button w="48%" variant="light" color="gray" onClick={closeCancelModal}>
                Keep Subscription
              </Button>
              <Button
                w="48%"
                color="red"
                loading={isCancelling}
                onClick={CancelSubscription}
                leftSection={<IconX size={16} />}
              >
                Cancel Subscription
              </Button>
            </div>
          </Stack>
        )}
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal
        opened={deleteAccountModalOpened}
        onClose={closeDeleteAccountModal}
        title={
          <Group gap="sm">
            <ThemeIcon variant="light" color="red" size="lg">
              <IconTrash size={20} />
            </ThemeIcon>
            <div>
              <Text size="lg" fw={600}>
                Delete Account
              </Text>
              <Text size="sm" c="dimmed">
                Are you sure you want to delete your account? This action cannot be undone.
              </Text>
            </div>
          </Group>
        }
        size="md"
        radius="md"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Stack gap="md">
          <Alert
            icon={<IconX size="1rem" />}
            title="Important Information"
            color="red"
            variant="light"
          >
            <Text size="sm" mb="xs">
              <b>What happens when you delete your account:</b>
            </Text>
            <Stack gap="xs">
              <Text size="sm">
                • All your personal data, including name, email, and payment information, will be
                permanently deleted.
              </Text>
              <Text size="sm">
                • Your account history, including transactions and subscriptions, will be removed.
              </Text>
              <Text size="sm">• No refunds will be issued for any unused services.</Text>
              <Text size="sm">• You will be logged out immediately.</Text>
            </Stack>
          </Alert>

          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" align="center">
              <Checkbox
                label="I understand that this action cannot be undone and I confirm the deletion of my account."
                checked={deleteCheckboxChecked}
                onChange={(event) => setDeleteCheckboxChecked(event.currentTarget.checked)}
                required
              />
            </Group>
            <TextInput
              label="Type 'I confirm the deletion of my account' to confirm"
              placeholder="I confirm the deletion of my account"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.currentTarget.value)}
              required
              error={
                deleteConfirmation !== "I confirm the deletion of my account" &&
                deleteConfirmation !== ""
              }
              mt="sm"
            />
          </Paper>

          <Divider />

          <Group justify="flex-end" gap="md">
            <Button variant="light" color="gray" onClick={closeDeleteAccountModal}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteAccountLoading}
              onClick={DeleteAccount}
              leftSection={<IconX size={16} />}
              disabled={
                !deleteCheckboxChecked ||
                deleteConfirmation !== "I confirm the deletion of my account"
              }
            >
              Yes, Delete Account
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default Account;
